// MemberPanel — 右ペインの参加者リスト。
//
// Phase 3 前半: members[] を name で表示するだけ。
// Phase 7 Task 7-9: `Perm.KICK` 保有者には各 member 行に kick ボタンを出す。
//   - 自分自身の行には出さない (`m.uid !== selfUid`).
//   - 同じ admin (Perm.KICK 保有) を target にしても server が
//     `cannot-kick-admin` で reject する (Task 7-4 確定方針). UI 側で
//     先回り gate しないのは、admin の現在 perms を都度 join 時しか取れない
//     (`state.permissions` は self 分のみ) ため. 万一 server が許してしまった
//     としても二重防御のため reject 経路を残す.
//   - クリック → ConfirmDialog → OK で `sendKick` (2026-05-21 ConfirmDialog 化、
//     旧 `window.confirm()` は iPad Safari で全画面モード解除を起こすため置換).
//
// Phase 7 Task 7-10b: kick ボタンと同列に「フィルタ」トグルボタンを追加。
//   - 表示条件: `m.uid !== selfUid` (= 自分の行には絶対に出さない).
//     **権限不要** (web ローカルフィルタ、protocol には乗らない).
//     self exclude の理由: 自分の `whiteboard.imagepaste` broadcast は
//     preview-as-ack の commit signal で、フィルタが当たると preview commit
//     が hang する (architecture.md §19.14, 7-10a 引き継ぎ).
//   - クリック → `onToggleFilter(uid)` (window.confirm なし、軽い操作なので
//     ワンクリック反転で良い).
//   - フィルタ ON 中の視覚区別: button を押下状態風に inset 表示 (kick ボタン
//     の outset と対照) + 文字色を `--color-accent` にして「ON」明示.
//
// custom-cursor-ux フォローアップ (2026-09-19、作者 staging フィードバック §A-1):
//   「入口が分かりづらい」への対応で、**参加者リストの自分の行**をカーソルエディタの
//   主導線にする:
//   - 自分の行のカーソルサムネ (確定済みがあれば) をクリック可能にし、クリックで
//     `onOpenCursorEditor` を呼ぶ. hover で鉛筆バッジ + title「自分のカーソルを描く」.
//   - 未作成 (サムネが無い) ときは自分の行にだけ点線枠の「＋」プレースホルダを出す
//     (他人の行は未作成なら何も出さない、既存どおり). これが「ここで作る」の合図.
//   - 初回だけの気づき: `showCursorEditorHint` が true の間、プレースホルダを数回
//     ゆっくり pulse させる (呼び出し側 `customCursorHint.ts` が localStorage
//     `oekaki.customCursor.hintSeen.v1` を見て判定、エディタを開いたら以後渡さない).
//
// Phase 6.7 R13 (consult 2026-05-05): 白背景 + 黒字化 + 文字装飾化.
//   - 旧版 `OekakiChatJava/screenshot/room.png` の参加者一覧は **白背景 + 黒テキスト**.
//   - 全体: `bg-white text-black` を追加. border 系は親 (`ClassicLayout` の `outsetFrame`) 任せ.
//   - h2 「参加者 (N)」: `text-black font-bold text-xs`.
//   - 緑 dot: 削除 (旧版になし).
//   - 「自分」accent badge → `(自分)` 文字表記に置換.
//   - ROM badge → `[ROM]` 文字表記に置換.
//   - 自分の行ハイライト (背景色) は削除し、`font-bold` のみで識別.
//   - フィルタボタン: outset / inset frame は維持. 色は白背景 + 黒文字ベースに.
//   - kick ボタン: 警告色 (`bg-[var(--color-error)]` + 白文字) は **維持** (破壊操作の視認性確保).
//
// (architecture.md §18.5 / §19.14 / docs/_wip/protocol-events.md §5.1 §6)

import { type CustomCursor, hasPerm, Perm, type RoomMember } from '@oekaki/protocol';
import { useState } from 'react';

import { ConfirmDialog } from '../../ConfirmDialog.js';
import { CustomCursorCanvas } from '../shared/CustomCursorCanvas.js';
import { decodeCursorPixels } from '../shared/customCursorPixels.js';

/** サムネ表示サイズ (CSS px、custom-cursor-2026-09-19.md §2.5-3 「16〜24 px」の下限寄り). */
const MEMBER_ROW_CURSOR_THUMB_SIZE_PX = 16;

/**
 * UI gate: 与えられた permissions / self uid から、対象 member 行に kick
 * ボタンを表示すべきかを決める純粋関数.
 *
 * - `Perm.KICK` 未保有なら常に false.
 * - 自分自身の行 (`memberUid === selfUid`) なら false.
 * - それ以外は true.
 *
 * 切り出し理由: vitest が node 環境で React component を直接テストできない
 * (`vitest.config.ts` 参照). pure helper を別途エクスポートして単体テストする.
 */
export const canKickMember = (permissions: number, selfUid: string, memberUid: string): boolean => {
  if (!hasPerm(permissions, Perm.KICK)) return false;
  if (memberUid === selfUid) return false;
  return true;
};

/**
 * Phase 7 Task 7-10b UI gate: 対象 member 行に「フィルタ」トグルを表示すべきか.
 *
 * - 自分自身の行なら false (= 自分を絶対 filter させない、preview commit
 *   hang 防止).
 * - それ以外は true (権限不要 — web ローカル filter).
 */
export const canShowFilterToggle = (selfUid: string, memberUid: string): boolean => {
  return memberUid !== selfUid;
};

export interface MemberPanelProps {
  readonly members: readonly RoomMember[];
  readonly selfUid: string;
  /**
   * Phase 7 Task 7-9: 自分の permissions bitmask. `Perm.KICK` を含まないなら
   * kick ボタンは一切表示しない. `state.permissions` をそのまま渡す想定.
   */
  readonly selfPermissions: number;
  /**
   * Phase 7 Task 7-9: kick 実行 callback. `MemberPanel` 内で ConfirmDialog
   * (2026-05-21 ConfirmDialog 化、iPad Safari 全画面解除対策) を出し、OK
   * だった場合のみ呼ぶ. Server rejection (`admin.kick-rejected`) は
   * reducer 側で system-line に出るので、ここでは callback 完了後の追加 UI
   * 反映は要らない.
   */
  readonly onKick: (target: { uid: string; displayName: string }) => void;
  /**
   * Phase 7 Task 7-10b: フィルタ ON の uid 集合. 含まれる行はトグルボタンが
   * ON 表示される. self は前段で除外済 (`canShowFilterToggle`) なので
   * 入っていても画面に出ないが、defensive にここで再確認する.
   */
  readonly filteredUids: ReadonlySet<string>;
  /**
   * Phase 7 Task 7-10b: フィルタトグル callback. クリックで toggle (反転).
   * `useFilteredUids` 経由で SessionProvider が provide.
   */
  readonly onToggleFilter: (uid: string) => void;
  /**
   * Phase 6.7 サブ task 後追い (consult 2026-05-07): 名前ダブルクリック →
   * chat input に displayName を挿入. ClassicLayout が ChatPanel の
   * `insertName(displayName)` を呼ぶ closure を渡す. 自分の行 (`uid === selfUid`)
   * でも呼ぶ (= 自分の名前を mention っぽく書きたいケースもあるかも).
   * 旧版踏襲で **「@」prefix なし、素の名前のみ**.
   */
  readonly onMemberDoubleClick?: (displayName: string) => void;
  /**
   * 2026-05-17 旧版 `■→` ボタン復活 (旧 `ChatPanel.java:294,1542-1553` 踏襲):
   * チャット + 参加者リストを bottom row から切り離して floating frame 化する
   * toggle. 旧版は left toolbar `PREVIEW` 直上にあったが、新版は **header
   * `参加者 (N)` 右隣** に配置 (= ユーザ提案 2026-05-17, header 右端の空きスペースを
   * 利用、chat 自体を操作する UI が chat の近くにある方が発見性が高い). detach 中は
   * MemberPanel が floating frame に乗るため、このボタンも一緒に浮いて元に戻せる.
   *
   * ラベルは旧版踏襲で dock 時 `■→` / detach 時 `×→`.
   */
  readonly chatDetached: boolean;
  readonly onToggleChatDetach: () => void;
  /**
   * 2026-05-31 alpha-feedback §3 follow-up: ChatPanel と同じ chatFontScale を
   * 受けて参加者リストの文字サイズも揃える (= bottom row の chat + member は
   * 同じ「social 系 panel」括りなので、文字スケールは揃ってないと違和感).
   * `undefined` のときは無指定 (= 等倍). ChatPanel の `fontScaleEm` と同 semantics.
   */
  readonly fontScaleEm?: number;
  /**
   * custom-cursor-2026-09-19.md §2.3/§2.5-3: uid → 編集中(draft)のカーソル.
   * `member.cursor-drafted` の transient state (`SessionView.cursorDrafts`
   * をそのまま渡せる形)。エントリがある間はそのサムネを**ライブ**表示し
   * 「作成中」を点滅させる。エントリが消えたら `members[].cursor` の
   * 確定値に自動で戻る (= このコンポーネントは単に優先順位を見るだけ)。
   */
  readonly cursorDrafts?: Readonly<Record<string, CustomCursor>>;
  /**
   * custom-cursor-ux フォローアップ (2026-09-19): 自分の行のカーソルサムネ / 「＋」
   * プレースホルダをクリックしたときにカーソルエディタを開く callback.
   * `undefined` ならクリック不可 (= 旧挙動、非表示のまま). 呼び出し側 (ClassicLayout /
   * ModernLayout) が「エディタを開く」単一の口を渡す.
   */
  readonly onOpenCursorEditor?: () => void;
  /**
   * custom-cursor-ux フォローアップ: まだ一度もカーソルエディタを開いたことが無い端末
   * (`customCursorHint.readCursorEditorHintSeen`) なら true. true の間だけ自分の行の
   * 「＋」プレースホルダを数回 pulse させる (= 発見性のワンショット演出).
   */
  readonly showCursorEditorHint?: boolean;
}

export const MemberPanel = ({
  members,
  selfUid,
  selfPermissions,
  onKick,
  filteredUids,
  onToggleFilter,
  onMemberDoubleClick,
  chatDetached,
  onToggleChatDetach,
  fontScaleEm,
  cursorDrafts,
  onOpenCursorEditor,
  showCursorEditorHint,
}: MemberPanelProps): React.ReactNode => {
  // 2026-05-21: kick 確認モーダル. 旧 `window.confirm()` は iPad Safari で
  // 全画面モード解除を起こすため ConfirmDialog に置換. target を memorize
  // しておき、OK で `onKick` を呼ぶ.
  const [kickTarget, setKickTarget] = useState<RoomMember | null>(null);
  const handleKickClick = (target: RoomMember): void => {
    setKickTarget(target);
  };

  return (
    <>
      {/* Phase 6.7 R13: 白背景 + 黒字化. border は親 (`ClassicLayout` の `outsetFrame`) で付くので
        ここでは持たない. */}
      <aside aria-label="参加者" className="flex h-full flex-col gap-2 bg-white p-2 text-black">
        {/* ヘッダー行: 「参加者 (N)」見出し + 右端に chat detach toggle.
          2026-05-17 (ユーザ提案): 旧版 `■→` ボタンを `header 右隣` に移植
          (= 旧版は left toolbar `PREVIEW` 直上). `参加者 (N)` テキスト後の空き
          スペースを活用し、chat 操作 UI を chat 近傍に配置することで発見性を上げる. */}
        <div className="flex items-center justify-between gap-1">
          {/* 表示専用ラベル. iPad Apple Pencil ジェスチャ抑止 (consult 2026-05-09). */}
          <h2
            className="select-none text-xs font-bold tracking-wide text-black"
            data-manual="together.members"
          >
            参加者 ({members.length})
          </h2>
          {/* 2026-05-17 旧版 `■→` ボタン復活 (旧 `ChatPanel.java:294,1542-1553`).
            label は旧版踏襲で dock 時 `■→` / detach 時 `×→`. フィルタ / kick
            ボタンと同じ小型 border スタイル (= MemberPanel 内の見た目統一). */}
          <button
            type="button"
            onClick={onToggleChatDetach}
            aria-pressed={chatDetached}
            data-manual="together.members"
            aria-label={chatDetached ? 'チャットを元の位置に戻す' : 'チャットを切り離す'}
            title={
              chatDetached
                ? 'チャットを元の位置に戻す'
                : 'チャット + 参加者リストを切り離して浮かせる'
            }
            className={
              chatDetached
                ? // 切り離し中 (押下相当): inset 風. フィルタ ON ボタンと同じ流儀.
                  'border border-black bg-white px-1 text-[10px] font-bold text-black shadow-[inset_1px_1px_0_rgba(0,0,0,0.45)]'
                : // 通常時 (= dock): outset 風. フィルタ OFF ボタンと同じ流儀.
                  'border border-black bg-white px-1 text-[10px] text-black shadow-[inset_1px_1px_0_rgba(255,255,255,0.6),inset_-1px_-1px_0_rgba(0,0,0,0.35)] hover:bg-gray-100'
            }
          >
            {chatDetached ? '×→' : '■→'}
          </button>
        </div>
        <ul
          className="flex flex-1 flex-col gap-1 overflow-y-auto text-xs"
          // 2026-05-31 alpha-feedback §3 follow-up: chat と同じ fontScaleEm を
          // 参加者リスト本体に inline style で適用. Tailwind の text-xs より
          // inline style の方が specificity 高いので確実に上書きされる.
          // (ChatPanel と同じ流儀、log container 直接当てに揃える)
          style={fontScaleEm !== undefined ? { fontSize: `${fontScaleEm}em` } : undefined}
        >
          {members.map((m) => {
            const isSelf = m.uid === selfUid;
            const showKick = canKickMember(selfPermissions, selfUid, m.uid);
            const showFilter = canShowFilterToggle(selfUid, m.uid);
            const isFiltered = filteredUids.has(m.uid);
            // custom-cursor-2026-09-19.md §2.5-3: draft (編集中) があればそれを
            // ライブ表示、無ければ確定済み cursor. どちらも無ければサムネ非表示.
            const draftCursor = cursorDrafts?.[m.uid];
            const isDrafting = draftCursor !== undefined;
            const displayCursor = draftCursor ?? m.cursor ?? null;
            const cursorPixels = displayCursor !== null ? decodeCursorPixels(displayCursor) : null;
            return (
              // Phase 6.7 R13: 自分の行ハイライト (背景色) は削除. `font-bold` のみで識別.
              // Phase 6.7 サブ task 後追い (consult 2026-05-07): ダブルクリックで
              // displayName を chat input に挿入する. li 全体に `onDoubleClick` を
              // つけることで、名前 / badge / ボタン以外の余白でも反応するように.
              // (テキスト二重選択は Tailwind の select-none で抑止する)
              <li
                key={m.uid}
                // 2026-05-17 displayName 改行抑止 + 省略: `li` の flex 親に `min-w-0` を当てる
                // ことで内部 `<span>` の `truncate` (= overflow-hidden + text-ellipsis +
                // whitespace-nowrap) が効くようにする. これが無いと flex item は内容幅で
                // 膨れて折り返さず横にはみ出す. badge / ボタンは shrink-0 で潰れない.
                className="flex min-w-0 select-none items-center gap-1 px-1 py-0.5"
                onDoubleClick={(e) => {
                  if (onMemberDoubleClick === undefined) return;
                  // ボタン (kick / フィルタ) の dblclick は意図的操作なので素通り.
                  // dblclick が button 上で発火することはほぼ無いが、defensive に
                  // target が button のとき名前挿入をスキップする.
                  if (e.target instanceof HTMLButtonElement) return;
                  onMemberDoubleClick(m.displayName);
                }}
                title={`ダブルクリックで名前をチャット欄に挿入: ${m.displayName}`}
              >
                {/* Phase 6.7 R13: 緑 dot は旧版になし → 削除. */}
                {/* custom-cursor-2026-09-19.md §2.5-3: カーソルサムネ. draft (編集中)
                  があればそれをライブ表示 (下の「作成中」点滅と対で「今まさに描いている」
                  ことを示す)、無ければ確定済み cursor.
                  custom-cursor-ux フォローアップ (2026-09-19): **自分の行だけ** クリック可能
                  にしてカーソルエディタを開く主導線にする. サムネが無い自分の行には点線
                  「＋」プレースホルダを出す (他人の行は従来どおり何も出さない). */}
                {cursorPixels !== null ? (
                  isSelf && onOpenCursorEditor !== undefined ? (
                    <button
                      type="button"
                      onClick={onOpenCursorEditor}
                      title="自分のカーソルを描く"
                      aria-label="自分のカーソルを描く"
                      data-testid="member-panel-self-cursor-thumb"
                      data-manual="together.custom-cursor"
                      className="group/cursor-thumb relative shrink-0"
                    >
                      <CustomCursorCanvas
                        pixels={cursorPixels}
                        sizePx={MEMBER_ROW_CURSOR_THUMB_SIZE_PX}
                        title="自分のカーソル"
                      />
                      {/* hover 鉛筆バッジ: 「編集できる」を示す装飾. サムネ自体の title は
                        aria-hidden の canvas ではなく button 側で担う. */}
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-1 -bottom-1 hidden h-2.5 w-2.5 items-center justify-center rounded-full bg-[var(--color-accent)] text-[6px] leading-none text-black group-hover/cursor-thumb:flex"
                      >
                        ✎
                      </span>
                    </button>
                  ) : (
                    <CustomCursorCanvas
                      pixels={cursorPixels}
                      sizePx={MEMBER_ROW_CURSOR_THUMB_SIZE_PX}
                      className="shrink-0"
                      title={`${m.displayName} のカーソル`}
                    />
                  )
                ) : (
                  isSelf &&
                  onOpenCursorEditor !== undefined && (
                    <button
                      type="button"
                      onClick={onOpenCursorEditor}
                      title="自分のカーソルを描く"
                      aria-label="自分のカーソルを描く"
                      data-testid="member-panel-self-cursor-placeholder"
                      data-manual="together.custom-cursor"
                      style={{
                        width: MEMBER_ROW_CURSOR_THUMB_SIZE_PX,
                        height: MEMBER_ROW_CURSOR_THUMB_SIZE_PX,
                        // styles.css `cursor-hint-pulse` keyframes (FeedbackButton の
                        // `feedback-bubble-fade` と同じ「inline style で keyframes 名を渡す」
                        // 流儀). 3 回だけゆっくり pulse し、以後は静止する (= うるさくしない).
                        animation:
                          showCursorEditorHint === true
                            ? 'cursor-hint-pulse 1.6s ease-in-out 3'
                            : undefined,
                      }}
                      className="flex shrink-0 items-center justify-center rounded-sm border border-dashed border-black/40 text-[9px] leading-none text-black/50 hover:border-black/70 hover:text-black/80"
                    >
                      ＋
                    </button>
                  )
                )}
                {isDrafting && (
                  <span
                    role="status"
                    className="shrink-0 animate-pulse text-[10px] text-[var(--color-accent)]"
                    aria-label={`${m.displayName} がカーソルを作成中`}
                  >
                    作成中
                  </span>
                )}
                {/* 2026-05-17 改行抑止 + 省略: 列幅が狭いとき displayName は折り返さず
                  `…` で省略する (= `truncate` クラス = overflow-hidden + text-ellipsis
                  + whitespace-nowrap). flex 親に `min-w-0` がある前提. `min-w-0` を
                  span 自身にも当てて 0 まで縮められるようにし、後続の badge / button
                  (= `(自分)` / `[ROM]` / フィルタ / kick) を押し出さない. */}
                <span
                  className={`min-w-0 truncate whitespace-nowrap ${
                    isSelf ? 'font-bold text-black' : 'text-black'
                  }`}
                >
                  {m.displayName}
                </span>
                {/* Phase 6.7 R13: 「自分」 badge → `(自分)` 文字表記に置換. */}
                {/* 2026-05-17: `shrink-0` で displayName 側の truncate に押されて潰れないよう固定. */}
                {isSelf && <span className="shrink-0 text-black">(自分)</span>}
                {/* Phase 6.7 R13: ROM badge → `[ROM]` 文字表記に置換. */}
                {m.isRom && <span className="shrink-0 text-black">[ROM]</span>}
                {/* Phase 7 Task 7-10b: フィルタトグル. 非 self 行に出る.
                  Phase 6.7 R13: 白背景 + 黒文字ベース. ON 中は inset 風で押下状態を明示.
                  ml-auto は ROM badge / 自分 badge が無い行で右寄せするため. */}
                {showFilter && (
                  <button
                    type="button"
                    onClick={() => onToggleFilter(m.uid)}
                    aria-pressed={isFiltered}
                    data-manual="together.filter"
                    aria-label={
                      isFiltered
                        ? `${m.displayName} のフィルタを解除`
                        : `${m.displayName} をフィルタ`
                    }
                    title={
                      isFiltered
                        ? `${m.displayName} のフィルタを解除 (絵: 以後の描画から復活 / チャット: 過去ログ含めて復活)`
                        : `${m.displayName} をフィルタ (絵: 以後非表示 / チャット: 過去ログ含めて非表示)`
                    }
                    className={
                      isFiltered
                        ? // ON: inset 風 (押下中を明示). 白背景 + 黒文字 + 暗い inset shadow.
                          // 2026-05-17: `shrink-0` で displayName 側 truncate に押されて潰れないよう固定.
                          'ml-auto shrink-0 border border-black bg-white px-1 text-[10px] font-bold text-black shadow-[inset_1px_1px_0_rgba(0,0,0,0.45)]'
                        : // OFF: outset 風. 白背景 + 黒文字.
                          'ml-auto shrink-0 border border-black bg-white px-1 text-[10px] text-black shadow-[inset_1px_1px_0_rgba(255,255,255,0.6),inset_-1px_-1px_0_rgba(0,0,0,0.35)] hover:bg-gray-100'
                    }
                  >
                    {isFiltered ? 'フィルタ解除' : 'フィルタ'}
                  </button>
                )}
                {showKick && (
                  <button
                    type="button"
                    onClick={() => handleKickClick(m)}
                    data-manual="room.kick"
                    // Phase 7 Task 7-9: 旧版風 outset frame に揃えるが、kick は
                    // 破壊的操作なので赤系 (`--color-error`) を背景に置く. 退室
                    // ボタン (ClassicLayout.tsx の Phase 6.7-B-fix-2A note) と
                    // ポリシーが分かれるが、kick は他人への作用なので警告色を残す.
                    // Phase 7 Task 7-10b: フィルタボタンが ml-auto を取った後は
                    // kick はその右隣に並ぶ (Tailwind: 後続 sibling は flex 自然順
                    // で右に出る).
                    // Phase 6.7 R13: kick は破壊操作の視認性確保のため赤系を維持.
                    aria-label={`${m.displayName} をキック`}
                    title={`${m.displayName} をキック`}
                    // 2026-05-17: `shrink-0` で displayName 側 truncate に押されて潰れないよう固定.
                    className="shrink-0 border border-black bg-[var(--color-error)] px-1 text-[10px] font-bold text-white hover:opacity-90"
                  >
                    kick
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </aside>
      {/* 2026-05-21: kick 確認モーダル. 旧 `window.confirm()` 置換 (iPad Safari
        全画面解除対策). target が null なら閉じている. destructive=true で
        赤系 OK ボタン. */}
      <ConfirmDialog
        open={kickTarget !== null}
        title="キック"
        message={kickTarget !== null ? `${kickTarget.displayName} をキックしますか?` : ''}
        confirmLabel="キック"
        destructive
        onConfirm={() => {
          if (kickTarget !== null) {
            onKick({ uid: kickTarget.uid, displayName: kickTarget.displayName });
          }
          setKickTarget(null);
        }}
        onCancel={() => setKickTarget(null)}
      />
    </>
  );
};
